import {
  InteractionStepType,
  InteractionStepConfig,
  IndexedInteractionStepResponse,
  ModelResponseEditorStepParams,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { ReviewLevel } from '@scale/shared-types';
import { IMultiTurnThreadHistory } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ThreadHistory.interface';
import { isConditionallyVisible, isStepVisible } from '@scale/shared/chat/api';

import { StepDescriptor } from '../stepDescriptor/StepDescriptor';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';
import { schema } from './validation';

import { ModelResponseEditorConfigurationComponent } from './ModelResponseEditorConfigurationComponent';
import { ModelResponseEditorViewComponent } from './ModelResponseEditorViewComponent';
import { ModelResponseEditorPinnedViewComponent } from './ModelResponseEditorPinnedViewComponent';

class ModelResponseEditorDescriptor extends StepDescriptor<InteractionStepType.ModelResponseEditor> {
  type = InteractionStepType.ModelResponseEditor;

  title = InteractionStepConfig[InteractionStepType.ModelResponseEditor].title;
  description = InteractionStepConfig[InteractionStepType.ModelResponseEditor].description;

  validationSchema = schema;

  ViewComponent = ModelResponseEditorViewComponent;
  PinnedViewComponent = ModelResponseEditorPinnedViewComponent;
  ConfigurationComponent = ModelResponseEditorConfigurationComponent;

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.ModelResponseEditor>,
    history: IMultiTurnThreadHistory,
    reviewLevel?: ReviewLevel,
  ) {
    if (
      !isStepVisible(stepParams, reviewLevel) ||
      !isConditionallyVisible(
        stepParams,
        history.responses,
        response.index,
        history.threadParams.templateVariables,
        reviewLevel,
      )
    ) {
      return { canSubmit: true, submitTooltip: '' };
    }

    if (!response.output || response.output.length === 0) {
      return {
        canSubmit: false,
        submitTooltip: 'Some model response editors have not been completed.',
      };
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  getResponseTemplatePaths() {
    return [
      {
        path: 'output',
        label: 'Edited Response',
        hideFromAnswerKey: true,
      },
    ];
  }

  generateEmptyResponse(
    _stepParams: StepParamsForType<InteractionStepType.ModelResponseEditor>,
    history: IMultiTurnThreadHistory,
  ) {
    const output = history.getMostRecentResponse();

    return {
      type: InteractionStepType.ModelResponseEditor,
      context: { baseResponse: output },
      output,
    };
  }

  getConfigurationPreviewTags(
    stepParams: StepParamsForType<InteractionStepType.ModelResponseEditor>,
  ) {
    const tags = super.getConfigurationPreviewTags(stepParams);
    const modelResponseEditorParams = stepParams as ModelResponseEditorStepParams;
    if (
      modelResponseEditorParams.params.response_constraints &&
      modelResponseEditorParams.params.response_constraints.length > 0
    ) {
      tags.push({
        value: 'Response Constraints',
        variant: 'info',
        icon: 'pen',
        tooltipText: 'This step contains response constraints',
      });
    }
    return tags;
  }
}

registerStepDescriptor({
  type: InteractionStepType.ModelResponseEditor,
  descriptor: new ModelResponseEditorDescriptor(),
});
